import { useEffect } from "react";
import { ProductSuggestionData } from './types';
import { buildImageSearchUrl } from 'shared/components/ImageSearch/helpers';
import { registerTool, unregisterTool } from 'shared/helpers/modelContext/toolRegistry';

// Helper function to remove duplicate words from a string (case-insensitive)
const removeDuplicateTerms = (text: string) => {
  if (!text) return '';
  const words = text.split(' ');
  const seenWords = new Set();
  const uniqueWords = [];

  for (const word of words) {
    const lowerWord = word.toLowerCase();
    if (!seenWords.has(lowerWord) && lowerWord !== 'null' && lowerWord !== 'undefined') {
      seenWords.add(lowerWord);
      uniqueWords.push(word);
    }
  }

  return uniqueWords.join(' ');
};

const ProductSuggestionTool = () => {
    useEffect(() => {
        const isRegistered = registerTool({
            name: "productSuggestion",
            description: `Extract product details from text and return structured JSON. Analyzes text descriptions to extract:
            - Gender (men/women/kid/boy/girl)
            - Colors (primary and secondary)
            - Brand, category, material
            - Extracted outfit items in searchable format
            - Suggested complementary outfit items (bottomwear, footwear, accessory)
            
            **STRICT RULES:**
            - Return valid JSON without markdown formatting
            - Use "men"/"women" not "male"/"female"
            - Return null for unknown values
            - Skip null values in formatted strings`,

            inputSchema: {
                type: "object",
                properties: {
                    prompt: {
                        type: "string",
                        description: "Text description of the outfit or product to analyze (e.g., 'Show me a black Nike t-shirt for men' or 'I want casual blue jeans with white sneakers')"
                    },
                    ai_generated_query: {
                        type: ["string", "null"],
                        description: "AI-generated search query from the prompt (optional)"
                    },
                    confidence_score: {
                        type: ["number", "null"],
                        description: "Confidence score for the AI-generated query (0.0 to 1.0). If > 0.9, the AI query will be used for search"
                    },
                    gender: {
                        type: "string",
                        enum: ["men", "women", "kid", "boy", "girl"],
                        description: "Gender category - must be one of: men, women, kid, boy, girl"
                    },
                    primary_color: {
                        type: "string",
                        description: "The dominant color mentioned in the outfit"
                    },
                    secondary_color: {
                        type: ["string", "null"],
                        description: "A secondary color if present, otherwise null"
                    },
                    brand: {
                        type: ["string", "null"],
                        description: "Brand name if mentioned, otherwise null"
                    },
                    category: {
                        type: ["string", "null"],
                        description: "Type of clothing (e.g., T-shirt, jeans, jacket, sneakers)"
                    },
                    material: {
                        type: ["string", "null"],
                        description: "Fabric type if mentioned (e.g., cotton, denim, leather, polyester)"
                    },
                    extracted_outfit: {
                        type: "object",
                        description: "Detected clothing items. Format: '<clothing_item>: <gender> <primary_color> <secondary_color> <brand> <material> <category>' (skip null values)",
                        additionalProperties: {
                            type: "string"
                        }
                    },
                    suggested_outfit: {
                        type: "object",
                        description: "Recommended complementary items for styling",
                        properties: {
                            bottomwear: {
                                type: ["object", "null"],
                                properties: {
                                    search_term: { type: "string", description: "Suggestive search term (e.g., 'Rugged Blue Jeans'), it's required" },
                                    category: { type: ["string", "null"], description: "Item category (e.g., 'Denim Jeans')" },
                                    type_of_wear: { type: ["string", "null"], description: "Occasion (e.g., 'casual wear', 'formal wear')" },
                                    description: { type: ["string", "null"], description: "Style description (max 50 words)" },
                                    color: { type: ["string", "null"], description: "Suggested color" },
                                    material: { type: ["string", "null"], description: "Suggested material" }
                                },
                                required: ["search_term"]
                            },
                            footwear: {
                                type: ["object", "null"],
                                properties: {
                                    search_term: { type: "string", description: "Suggestive search term (e.g., 'White Sneakers'), it's required" },
                                    category: { type: ["string", "null"], description: "Item category (e.g., 'Sneakers')" },
                                    type_of_wear: { type: ["string", "null"], description: "Occasion (e.g., 'casual wear', 'streetwear')" },
                                    description: { type: ["string", "null"], description: "Style description (max 50 words)" },
                                    color: { type: ["string", "null"], description: "Suggested color" },
                                    material: { type: ["string", "null"], description: "Suggested material" }
                                },
                                required: ["search_term"]
                            },
                            accessory: {
                                type: ["object", "null"],
                                properties: {
                                    search_term: { type: "string", description: "Suggestive search term (e.g., 'Leather Watch'), it's required" },
                                    category: { type: ["string", "null"], description: "Item category (e.g., 'Watch', 'Sunglasses')" },
                                    type_of_wear: { type: ["string", "null"], description: "Occasion (e.g., 'party wear', 'daily wear')" },
                                    description: { type: ["string", "null"], description: "Style description (max 50 words)" },
                                    color: { type: ["string", "null"], description: "Suggested color" },
                                    material: { type: ["string", "null"], description: "Suggested material" }
                                },
                                required: ["search_term"]
                            }
                        }
                    }
                },
                required: ["prompt", "gender", "primary_color", "extracted_outfit", "suggested_outfit"]
            },

            annotations: {
                title: "Product Suggestion",
                description: "Extract outfit details from text description and log to console",
                readOnlyHint: true
            },

            execute: async function (data: any) {
                try {
                    // Remove the prompt field before logging
                    const { prompt, ai_generated_query, confidence_score, ...suggestionData } = data;
                    
                    // Print the data (as user requested)
                    console.log(data);

                    // Build search URL similar to builtInOutput logic
                    let searchTerm = '';
                    let aiQuery = ai_generated_query || null;
                    let confidence = confidence_score || 0;
                    let recommendations = '';
                    const {
                        extracted_outfit,
                        suggested_outfit,
                        gender,
                        material,
                        primary_color,
                        category,
                        brand,
                    } = suggestionData || {};

                    // Determine which query to use based on confidence score
                    if (aiQuery && confidence > 0.9) {
                        // Use AI-generated query if confidence is high
                        searchTerm = aiQuery;
                    } else {
                        // Build the search term from extracted attributes
                        const searchParts: string[] = [];

                        if (gender) searchParts.push(gender);
                        if (primary_color) searchParts.push(primary_color);
                        if (brand) searchParts.push(brand);
                        if (category) searchParts.push(category);
                        if (material) searchParts.push(material);

                        // If any of the key fields are missing, check extracted_outfit
                        if (!gender || !primary_color || !brand || !category || !material) {
                            if (extracted_outfit) {
                                Object.values(extracted_outfit).forEach((value) => {
                                    if (
                                        typeof value === 'string' &&
                                        value &&
                                        value !== 'null' &&
                                        value !== 'undefined'
                                    ) {
                                        searchParts.push(value);
                                    }
                                });
                            }
                        }

                        searchTerm = searchParts
                            .filter((word) => word && word !== 'null' && word !== 'undefined')
                            .join(' ')
                            .replace(/\s+/g, ' ')
                            .trim();
                    }

                    // Prepare recommendations
                    if (suggested_outfit) {
                        recommendations = encodeURIComponent(JSON.stringify(suggested_outfit));
                    }

                    // Store data in localStorage
                    localStorage.setItem('suggestions', JSON.stringify(suggestionData));
                    localStorage.setItem('imageToProcess', 'true');

                    // Build URL params
                    const baseUrl = '/image-search/';
                    const params: any = {
                        q: removeDuplicateTerms(searchTerm),
                        searchType: 'ManualSearch',
                        internalSearchTerm: removeDuplicateTerms(searchTerm),
                        typedSearchTerm: removeDuplicateTerms(searchTerm),
                        gender,
                        material,
                        color: primary_color,
                        category,
                        recommendations,
                        source: 'builtIn',
                    };
                    
                    // Add AI query and confidence to params if available
                    if (aiQuery) {
                        params.ai_query = aiQuery;
                    }
                    if (confidence > 0) {
                        params.confidence = confidence;
                    }

                    const dynamicURL = buildImageSearchUrl(baseUrl, params);
                    const queryInfo = aiQuery && confidence > 0.9 
                        ? `\n\nUsing AI Query (Confidence: ${(confidence * 100).toFixed(1)}%): ${aiQuery}`
                        : aiQuery 
                            ? `\n\nAI Query available but low confidence (${(confidence * 100).toFixed(1)}%). Using attribute-based query instead.`
                            : '';

                    // Schedule navigation after response is returned (WebMCP best practice)
                    // Using setTimeout ensures the agent receives the response before navigation
                    setTimeout(() => {
                        window.location.href = dynamicURL + "&from=productSuggestionTool";
                    }, 100);

                    // Return structured response BEFORE navigation
                    return {
                        content: [{
                            type: "text",
                            text: `Product suggestion data extracted from: "${prompt}"\n\nSearch Term: ${searchTerm}${queryInfo}\n\nNavigating to: ${dynamicURL}\n\nData stored in localStorage for image search processing.`
                        }]
                    };

                } catch (error: any) {
                    return {
                        content: [{
                            type: "text",
                            text: `Error in productSuggestion tool: ${error.message}`
                        }],
                        isError: true
                    };
                }
            }
        });

        // Cleanup function
        return () => {
            if (isRegistered) {
                unregisterTool("productSuggestion");
            }
        };
    }, []);

    return null;
};

export default ProductSuggestionTool;
